import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shield, KeyRound, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { formatErr } from "../lib/auth";
import TopNav from "../components/TopNav";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Reset link is missing the token parameter.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <TopNav landing />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md flat-card p-8 scanlines" data-testid="reset-card">
          <div className="flex items-center gap-3 mb-2">
            <Shield size={22} color="var(--cyan)" strokeWidth={1.5} />
            <div className="overline">// CREDENTIAL_ROTATION</div>
          </div>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight mb-1">Set New Credential</h1>
          <p className="text-sm text-[var(--text-2)] mb-6">
            Choose a new password. All other active sessions on this account will be terminated upon completion.
          </p>

          {done && (
            <div className="border border-[var(--green)] p-4 flex items-start gap-3" data-testid="reset-success">
              <CheckCircle2 size={20} color="var(--green)" strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-display font-bold uppercase text-sm text-[var(--green)]">Credential Updated</div>
                <p className="text-sm text-[var(--text-2)] mt-1">
                  Redirecting to login… <Link to="/login" className="text-[var(--cyan)] hover:underline">Go now</Link>
                </p>
              </div>
            </div>
          )}

          {!done && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">New Password</label>
                <input
                  type="password" required minLength={6}
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  className="input-tac mt-1" placeholder="••••••••"
                  data-testid="reset-password"
                  autoFocus
                />
              </div>
              <div>
                <label className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)]">Confirm Password</label>
                <input
                  type="password" required minLength={6}
                  value={pw2} onChange={(e) => setPw2(e.target.value)}
                  className="input-tac mt-1" placeholder="••••••••"
                  data-testid="reset-password-confirm"
                />
              </div>

              {err && (
                <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="reset-error">
                  {err}
                </div>
              )}

              <button type="submit" disabled={busy || !token} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="reset-submit">
                <KeyRound size={14} />
                {busy ? "Rotating…" : "Set New Credential"}
              </button>
            </form>
          )}

          <div className="mt-6 border-t border-[var(--border)] pt-4 text-sm text-[var(--text-2)]">
            <Link to="/login" className="text-[var(--cyan)] hover:underline" data-testid="reset-to-login">Return to login</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
